import dayjs from "dayjs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ReceiptThumb } from "@/components/ReceiptThumb";
import { fmtTime, roundQuarter } from "@/lib/format";
import { AlertTriangle, RotateCcw } from "lucide-react";

// Shift review panels for the Approval Workbench: approved pay vs billed times,
// awake entries (sleepovers) and expense decisions. The hours shown come from
// the backend preview, which uses the same calculation as approval and the
// payroll export.

export const toLocal = (v) => (v ? dayjs(v).format("YYYY-MM-DDTHH:mm") : "");
export const toStore = (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm:ss") : null);
const entryKey = (e, i) => String(e.id || i);

/** Initial review state for a shift (approved pay defaults to the rounded clock times). */
export function initReview(shift, expenses = [], defaultGroup = "") {
  const payStart = shift.payroll_start ? dayjs(shift.payroll_start) : roundQuarter(shift.actual_clock_in);
  const payEnd = shift.payroll_end ? dayjs(shift.payroll_end) : roundQuarter(shift.actual_clock_out);
  const expDec = {};
  expenses.filter((e) => e.status === "Pending").forEach((e) => {
    expDec[e.idx] = { decision: null, reject_reason: "", company_absorb: false, absorb_reason: "", bill_group: defaultGroup };
  });
  return {
    pay_start: payStart ? payStart.format("YYYY-MM-DDTHH:mm") : "",
    pay_end: payEnd ? payEnd.format("YYYY-MM-DDTHH:mm") : "",
    bill_start: toLocal(shift.start_time), bill_end: toLocal(shift.end_time), bill_reason: "",
    awake: null,            // filled from the first preview: {key: {start, end, status}}
    awake_adjust_reason: "", awake_reject_reason: "",
    expDec,
  };
}

/** Seed per-entry awake state from the preview's entries. */
export function awakeStateFrom(entries) {
  const m = {};
  (entries || []).forEach((e, i) => {
    m[entryKey(e, i)] = { start: toLocal(e.start), end: toLocal(e.end), status: e.status || null,
                          orig_start: toLocal(e.start), orig_end: toLocal(e.end) };
  });
  return m;
}

const billChanged = (r, shift) => toStore(r.bill_start) !== (shift.start_time || "") || toStore(r.bill_end) !== (shift.end_time || "");
const awakeTimesChanged = (r) => Object.values(r.awake || {}).some((a) => a.start !== a.orig_start || a.end !== a.orig_end);
const awakeRejected = (r) => Object.values(r.awake || {}).some((a) => a.status === "Rejected");

/** Approve / preview request body from the review state. */
export function buildBody(r, shift, entries, { withReasons = true } = {}) {
  const body = { payroll_start: toStore(r.pay_start), payroll_end: toStore(r.pay_end) };
  if (billChanged(r, shift)) {
    body.rostered_start = toStore(r.bill_start);
    body.rostered_end = toStore(r.bill_end);
    if (withReasons) body.bill_reason = r.bill_reason.trim();
  }
  if (r.awake && entries?.length) {
    body.awake_entries = entries.map((e, i) => {
      const a = r.awake[entryKey(e, i)] || {};
      const d = { id: e.id || String(i), status: a.status };
      if (a.start !== a.orig_start) d.start = toStore(a.start);
      if (a.end !== a.orig_end) d.end = toStore(a.end);
      return d;
    });
    if (withReasons) {
      body.awake_adjust_reason = r.awake_adjust_reason.trim();
      body.awake_reject_reason = r.awake_reject_reason.trim();
    }
  }
  return body;
}

/** Decisions still to make before the shift can be approved (empty = ready). */
export function pendingDecisions(r, expenses) {
  if (!r) return [];
  const out = [];
  const awake = Object.values(r.awake || {}).filter((a) => !a.status).length;
  if (awake) out.push(`${awake} awake time entr${awake === 1 ? "y" : "ies"}`);
  const exp = expenses.filter((e) => e.status === "Pending" && !(r.expDec[e.idx] || {}).decision).length;
  if (exp) out.push(`${exp} expense claim${exp === 1 ? "" : "s"}`);
  return out;
}

/** Client-side checks before approving; returns an error message or null. */
export function validateReview(r, shift, expenses, expMeta) {
  if (!r.pay_start || !r.pay_end) return "Enter the paid start and end time";
  if (!dayjs(r.pay_end).isAfter(dayjs(r.pay_start))) return "The paid end must be after the paid start";
  if (billChanged(r, shift)) {
    if (!dayjs(r.bill_end).isAfter(dayjs(r.bill_start))) return "The billed end must be after the billed start";
    if (!r.bill_reason.trim()) return "Add a reason for changing the billed (rostered) times";
  }
  const pend = pendingDecisions(r, expenses);
  if (pend.length) return `Approve or reject ${pend.join(" and ")} first`;
  if (awakeTimesChanged(r) && !r.awake_adjust_reason.trim()) return "Add a reason for changing the awake time";
  if (awakeRejected(r) && !r.awake_reject_reason.trim()) return "Add a reason for rejecting awake time";
  for (const e of expenses.filter((x) => x.status === "Pending")) {
    const d = r.expDec[e.idx] || {};
    if (d.decision === "reject" && !d.reject_reason?.trim()) return "Add a reason to reject an expense claim";
    if (d.decision === "approve" && !expMeta.pt && !d.company_absorb) return "Confirm the company will absorb the non-billable expense, or reject it";
    if (d.decision === "approve" && !expMeta.pt && !d.absorb_reason?.trim()) return "Add a reason why the company is paying the expense claim";
  }
  return null;
}

const KIND = {
  worked: { label: "Worked", cls: "text-slate-700" },
  sleepover: { label: "Sleepover (inactive)", cls: "text-indigo-600" },
  active: { label: "Active work during sleepover", cls: "text-amber-700" },
};

const TimeInput = ({ value, onChange, testid, disabled, step = 900 }) => (
  <Input type="datetime-local" step={step} value={value || ""} disabled={disabled}
    onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" data-testid={testid} />
);

/** Approved pay (clock) and billed (rostered) times, with what goes to payroll. */
export function HoursPanel({ shift, review, setReview, summary, editable }) {
  const set = (patch) => setReview((r) => ({ ...r, ...patch }));
  // Another worker's timesheet on a multi-worker shift: hours bill once, on the
  // primary worker's timesheet, so the billed times aren't edited here.
  const billEditable = editable && !shift.timesheet_worker;
  const bChanged = billEditable && billChanged(review, shift);
  const segs = summary?.segments || [];
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2.5" data-testid="hours-panel">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Hours</div>
      <div className="grid grid-cols-[3.5rem_1fr_1fr] gap-x-2 gap-y-1 items-center">
        <div />
        <div className="text-[10px] text-slate-400">Start</div>
        <div className="text-[10px] text-slate-400">End</div>

        <div className="font-semibold text-emerald-700">Pay</div>
        {editable ? (<>
          <TimeInput value={review.pay_start} onChange={(v) => set({ pay_start: v })} testid="pay-start" />
          <TimeInput value={review.pay_end} onChange={(v) => set({ pay_end: v })} testid="pay-end" />
        </>) : (<>
          <div className="tabular-nums">{fmtTime(shift.payroll_start)}</div>
          <div className="tabular-nums">{fmtTime(shift.payroll_end)}</div>
        </>)}
        <div />
        <div className="col-span-2 text-[10px] text-slate-500" data-testid="pay-clocked">
          Clocked {fmtTime(shift.actual_clock_in) || "—"}–{shift.actual_clock_out ? fmtTime(shift.actual_clock_out) : "missing"}
          {(shift.actual_clock_in_office_entered || shift.actual_clock_out_office_entered) ? " (office-entered)" : ""}
          {editable && (<>
            {" · "}<button type="button" className="text-brand hover:underline" data-testid="pay-use-rostered-start"
              onClick={() => set({ pay_start: review.bill_start })}>Pay from rostered start</button>
            {" · "}<button type="button" className="text-brand hover:underline" data-testid="pay-use-rostered-end"
              onClick={() => set({ pay_end: review.bill_end })}>Pay to rostered end</button>
          </>)}
        </div>

        <div className="font-semibold text-brand mt-1">Bill</div>
        {billEditable ? (<>
          <TimeInput value={review.bill_start} onChange={(v) => set({ bill_start: v })} testid="bill-start" />
          <TimeInput value={review.bill_end} onChange={(v) => set({ bill_end: v })} testid="bill-end" />
        </>) : (<>
          <div className="tabular-nums mt-1">{fmtTime(summary?.bill_start || shift.start_time)}</div>
          <div className="tabular-nums mt-1">{fmtTime(summary?.bill_end || shift.end_time)}</div>
        </>)}
        <div />
        <div className="col-span-2 text-[10px] text-slate-500">
          Rostered {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
          {shift.billing_status === "Adjusted" && " · billing adjusted by the office"}
          {shift.timesheet_worker && <span data-testid="bill-on-primary"> · billed once, on the primary worker's timesheet</span>}
          {billEditable && (<>
            {" · "}<button type="button" className="text-brand hover:underline" data-testid="bill-late-arrival"
              title="The worker arrived late: bill the participant from the paid start"
              onClick={() => set({ bill_start: review.pay_start })}>Late arrival: bill from paid start</button>
          </>)}
        </div>
      </div>
      {bChanged && (
        <Input value={review.bill_reason} onChange={(e) => set({ bill_reason: e.target.value })}
          placeholder="Reason for changing the billed times (required), e.g. worker arrived late"
          className="h-8 text-xs" data-testid="bill-reason" />
      )}

      <div className="grid grid-cols-3 gap-2 text-center pt-1">
        <div><div className="text-[10px] text-slate-400">Paid (hours worked)</div>
          <div className="text-sm font-bold tabular-nums text-emerald-700" data-testid="sum-paid">{summary ? `${summary.paid_hours}h` : "…"}</div></div>
        <div><div className="text-[10px] text-slate-400">Sleepover (inactive)</div>
          <div className="text-sm font-bold tabular-nums text-indigo-600" data-testid="sum-sleepover">{summary ? `${summary.sleepover_hours}h` : "…"}</div></div>
        <div><div className="text-[10px] text-slate-400">Billed (active)</div>
          <div className="text-sm font-bold tabular-nums text-brand" data-testid="sum-billed">{summary ? (summary.billed_on_primary ? "—" : `${summary.billed_active_hours}h`) : "…"}</div>
          {summary && (summary.billed_sleepovers > 0 || summary.billed_awake_hours > 0) && (
            <div className="text-[10px] text-brand" data-testid="sum-billed-extra">
              {summary.billed_sleepovers > 0 && "+ 1 sleepover"}
              {summary.billed_awake_hours > 0 && ` + ${summary.billed_awake_hours}h awake`}
            </div>
          )}</div>
      </div>
      {segs.length > 0 && (
        <div data-testid="pay-segments">
          <div className="text-[10px] text-slate-400 mb-0.5">Sent to payroll</div>
          <div className="space-y-0.5">
            {segs.map((s, i) => (
              <div key={i} className="flex justify-between gap-2 tabular-nums">
                <span className={KIND[s.kind]?.cls}>{KIND[s.kind]?.label || s.kind}</span>
                <span className="text-slate-600">{fmtTime(s.start)}–{fmtTime(s.end)} · {s.hours}h</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Pay Cat applies minimum engagement and the 1-hour minimum for active work.</div>
        </div>
      )}
      {(summary?.warnings || []).map((w, i) => (
        <div key={i} className="text-[11px] text-amber-700 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{w}</div>
      ))}
    </div>
  );
}

/** Sleepover awake entries: approve, reject or adjust each one. */
export function AwakePanel({ shift, entries, review, setReview, editable, onReturn }) {
  const setEntry = (k, patch) => setReview((r) => ({ ...r, awake: { ...r.awake, [k]: { ...r.awake[k], ...patch } } }));
  const list = entries || [];
  const st = review.awake || {};
  const approvedMin = list.reduce((acc, e, i) => {
    const a = st[entryKey(e, i)];
    if (!a || a.status !== "Approved") return acc;
    return acc + Math.max(0, dayjs(a.end).diff(dayjs(a.start), "minute"));
  }, 0);
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50 p-2.5" data-testid="detail-awake">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Awake time (sleepover)</div>
        {list.length > 0 && (
          <span className="text-[10px] text-indigo-700 font-semibold">
            {approvedMin} min approved{editable && list.some((e, i) => !st[entryKey(e, i)]?.status) ? " · decide each entry" : ""}
          </span>
        )}
      </div>
      {list.length === 0 ? <div className="text-slate-500">No awake time claimed.</div> : (
        <div className="space-y-1.5">
          {list.map((e, i) => {
            const k = entryKey(e, i);
            const a = st[k] || { start: toLocal(e.start), end: toLocal(e.end), status: e.status };
            const edited = a.start !== a.orig_start || a.end !== a.orig_end;
            const rejected = a.status === "Rejected";
            return (
              <div key={k} className={`rounded px-2 py-1.5 ${rejected ? "bg-red-50 border border-red-200" : !a.status && editable ? "bg-white border border-amber-300" : "bg-indigo-100"}`} data-testid={`detail-awake-entry-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {editable ? (
                    <div className="flex items-center gap-1">
                      <TimeInput value={a.start} onChange={(v) => setEntry(k, { start: v })} testid={`awake-${i}-start`} step={300} />
                      <span>–</span>
                      <TimeInput value={a.end} onChange={(v) => setEntry(k, { end: v })} testid={`awake-${i}-end`} step={300} />
                    </div>
                  ) : (
                    <span className="text-slate-700 tabular-nums">{fmtTime(e.start)}–{fmtTime(e.end)} · {Math.round(e.minutes || 0)} min</span>
                  )}
                  {editable ? (
                    <div className="flex gap-1">
                      {[["Approved", "Approve", "bg-emerald-600"], ["Rejected", "Reject", "bg-red-600"]].map(([v, lbl, bg]) => (
                        <button key={v} type="button" onClick={() => setEntry(k, { status: v })} data-testid={`awake-${i}-${v.toLowerCase()}`}
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${a.status === v ? `${bg} text-white border-transparent` : "bg-white text-slate-500 border-slate-200"}`}>{lbl}</button>
                      ))}
                    </div>
                  ) : (
                    <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${e.status === "Rejected" ? "bg-red-100 text-red-700" : (e.status === "Approved" || shift.awake_approved) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {e.status || (shift.awake_approved ? "Approved" : "Pending")}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {e.reason === "Other" ? (e.reason_other || "Other") : (e.reason || "No reason given")}
                  {(edited || e.original_start) && <> · worker logged {fmtTime(e.original_start || a.orig_start)}–{fmtTime(e.original_end || a.orig_end)}</>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editable && awakeTimesChanged(review) && (
        <Input value={review.awake_adjust_reason} onChange={(ev) => setReview((r) => ({ ...r, awake_adjust_reason: ev.target.value }))}
          placeholder="Reason for changing the awake time (required)" className="h-8 text-xs mt-2" data-testid="awake-adjust-reason" />
      )}
      {editable && awakeRejected(review) && (
        <Input value={review.awake_reject_reason} onChange={(ev) => setReview((r) => ({ ...r, awake_reject_reason: ev.target.value }))}
          placeholder="Reason for rejecting awake time (required — the worker is told)" className="h-8 text-xs mt-2" data-testid="awake-reject-reason" />
      )}
      {!editable && shift.awake_reject_reason && <div className="text-[11px] text-red-600 mt-1">Rejected: “{shift.awake_reject_reason}”</div>}
      {editable && list.length > 0 && onReturn && (
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-amber-700 border-amber-300 mt-2" data-testid="detail-awake-return" onClick={onReturn}>
          <RotateCcw className="h-3 w-3 mr-1" />Return awake time to worker
        </Button>
      )}
    </div>
  );
}

/** Expense claims, with approve / reject decisions while the timesheet is open. */
export function ExpensePanel({ shiftName, expenses, meta, review, setReview, editable }) {
  const setDec = (idx, patch) => setReview((r) => ({ ...r, expDec: { ...r.expDec, [idx]: { ...(r.expDec[idx] || { decision: null }), ...patch } } }));
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
        Expense claims {editable && <span className={meta.pt ? "text-emerald-600" : "text-amber-600"}>{meta.pt ? "· bills to Provider Travel" : "· no Provider Travel item"}</span>}
      </div>
      <div className="space-y-1.5" data-testid="detail-expenses">
        {expenses.map((e) => {
          const pending = e.status === "Pending";
          const d = review.expDec[e.idx] || { decision: null };
          return (
            <div key={e.idx} className="rounded-md bg-slate-50 border border-slate-200 p-2" data-testid={`detail-expense-${e.idx}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">${Number(e.amount).toFixed(2)} <span className="font-normal text-slate-500">— {e.reason}</span></span>
                <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${e.status === "Approved" ? "bg-emerald-100 text-emerald-700" : e.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{e.status}</span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                {e.has_receipt && <ReceiptThumb shiftName={shiftName} idx={e.idx} name={e.receipt_name} size={44} />}
                {e.status === "Approved" && <span className="text-[10px] text-slate-500" data-testid={`detail-expense-${e.idx}-dest`}>{e.billed ? `Billed to Provider Travel${e.bill_group ? ` · ${e.bill_group}` : ""}` : e.company_absorbed ? `Company absorbed${e.absorb_reason ? ` — “${e.absorb_reason}”` : ""}` : ""}</span>}
                {e.status === "Rejected" && e.reject_reason && <span className="text-[10px] text-red-500">“{e.reject_reason}”</span>}
              </div>
              {editable && pending && (<>
                <div className="flex gap-2 mt-1.5">
                  {[["approve", "Approve", "bg-emerald-600"], ["reject", "Reject", "bg-red-600"]].map(([v, lbl, bg]) => (
                    <button key={v} type="button" onClick={() => setDec(e.idx, { decision: v })} data-testid={`expense-${e.idx}-${v}`}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded border ${d.decision === v ? `${bg} text-white border-transparent` : "bg-white text-slate-500 border-slate-200"}`}>{lbl}</button>
                  ))}
                </div>
                {d.decision === "reject" && (
                  <Input value={d.reject_reason || ""} onChange={(ev) => setDec(e.idx, { reject_reason: ev.target.value })} placeholder="Reason for rejection" className="h-8 text-xs mt-1.5" data-testid={`expense-${e.idx}-reject-reason`} />
                )}
                {d.decision === "approve" && (meta.groups || []).length > 0 && (
                  <div className="mt-1.5" data-testid={`expense-${e.idx}-bill-group-row`}>
                    <div className="text-[10px] text-slate-500 mb-0.5">Bills to</div>
                    <select value={d.bill_group || meta.defaultGroup || ""} onChange={(ev) => setDec(e.idx, { bill_group: ev.target.value })}
                      data-testid={`expense-${e.idx}-bill-group`} className="w-full h-8 text-[11px] rounded border border-slate-200 bg-white px-2 text-slate-700">
                      {meta.groups.map((g) => (
                        <option key={g.group} value={g.group}>{g.group}{g.participant_name ? ` · ${g.participant_name}` : ""}{g.billing_off ? " (no charge line)" : ""}</option>
                      ))}
                    </select>
                  </div>
                )}
                {d.decision === "approve" && !meta.pt && (
                  <div className="mt-1.5 rounded border border-amber-200 bg-amber-50 p-2 space-y-1.5">
                    <label className="flex items-start gap-2 text-[11px] text-amber-800 cursor-pointer" data-testid={`expense-${e.idx}-absorb-label`}>
                      <input type="checkbox" checked={!!d.company_absorb} onChange={(ev) => setDec(e.idx, { company_absorb: ev.target.checked })}
                        data-testid={`expense-${e.idx}-absorb`} className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600 cursor-pointer" />
                      I confirm the company will incur this cost and it will not be reimbursed (no Provider Travel to bill).
                    </label>
                    {d.company_absorb && (
                      <Input value={d.absorb_reason || ""} onChange={(ev) => setDec(e.idx, { absorb_reason: ev.target.value })}
                        placeholder="Why is the company paying this instead of the participant? (required)"
                        className="h-8 text-xs bg-white" data-testid={`expense-${e.idx}-absorb-reason`} />
                    )}
                  </div>
                )}
              </>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
